import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, ChevronLeft, ChevronRight, Disc3, Headphones, Apple, Youtube } from "lucide-react";
import { fileUrl } from "@/lib/api";

const SLIDES = [
  { platform: "Spotify", handle: "PRODRUSHI", url: "https://open.spotify.com/artist/1fVSxNbesvw20EEMIUc4OU", img: "/api/files/beatsbyatl/media/banner_spotify.jpg", icon: Disc3, cta: "Stream on Spotify" },
  { platform: "SoundCloud", handle: "producedbyrushi", url: "https://soundcloud.com/producedbyrushi", img: "/api/files/beatsbyatl/media/banner_soundcloud.webp", icon: Headphones, cta: "Stream on SoundCloud", imgClass: "object-[center_55%]" },
  { platform: "YouTube", handle: "@prodrushi", url: "https://youtube.com/@prodrushi", img: "/api/files/beatsbyatl/media/banner_youtube_qr.webp", icon: Youtube, cta: "Watch on YouTube" },
  { platform: "Apple Music", handle: "PRODRUSHI", url: "https://music.apple.com/us/artist/prodrushi/1796445650", img: "/api/files/beatsbyatl/media/banner_applemusic.png", icon: Apple, cta: "Stream on Apple Music" },
];

export const PlatformCarousel = () => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const go = useCallback((dir) => setIndex((i) => (i + dir + SLIDES.length) % SLIDES.length), []);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => go(1), 4500);
    return () => clearInterval(t);
  }, [paused, go]);

  const slide = SLIDES[index];
  const Icon = slide.icon;

  return (
    <div
      className="relative h-[380px] overflow-hidden rounded-3xl border border-white/10 sm:h-[440px]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="platform-carousel"
    >
      <AnimatePresence mode="popLayout">
        <motion.img
          key={index}
          src={fileUrl(slide.img)}
          alt={`${slide.platform} — ${slide.handle}`}
          initial={{ opacity: 0, scale: 1.06 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className={`absolute inset-0 h-full w-full object-cover ${slide.imgClass || ""}`}
        />
      </AnimatePresence>
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-black/30" />

      <a
        href={slide.url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`platform-slide-link-${slide.platform.toLowerCase()}-${slide.handle.replace(/[^a-z0-9]/gi, "").toLowerCase()}`}
        className="group absolute inset-0 flex flex-col items-center justify-center text-center"
      >
        <span className="flex h-14 w-14 items-center justify-center rounded-full border border-emerald-300/40 bg-black/50 text-emerald-200 backdrop-blur-md transition-transform group-hover:scale-110">
          <Icon size={22} />
        </span>
        <span className="font-heading mt-5 text-4xl font-extrabold tracking-tight text-white sm:text-5xl" data-testid="platform-slide-name">
          {slide.platform}
        </span>
        <span className="mt-1 font-mono text-xs uppercase tracking-widest text-emerald-200">{slide.handle}</span>
        <span className="mt-6 flex items-center gap-2 rounded-full bg-emerald-300 px-6 py-3 text-xs font-bold uppercase tracking-wider text-black opacity-0 transition-all duration-300 group-hover:opacity-100">
          {slide.cta} <ArrowUpRight size={14} />
        </span>
      </a>

      <button
        onClick={() => go(-1)}
        data-testid="platform-carousel-prev"
        className="absolute left-4 top-1/2 z-10 -translate-y-1/2 rounded-full border border-white/15 bg-black/50 p-2.5 text-white backdrop-blur-md transition-colors hover:border-emerald-300/50 hover:text-emerald-200"
      >
        <ChevronLeft size={18} />
      </button>
      <button
        onClick={() => go(1)}
        data-testid="platform-carousel-next"
        className="absolute right-4 top-1/2 z-10 -translate-y-1/2 rounded-full border border-white/15 bg-black/50 p-2.5 text-white backdrop-blur-md transition-colors hover:border-emerald-300/50 hover:text-emerald-200"
      >
        <ChevronRight size={18} />
      </button>

      <div className="absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 gap-2" data-testid="platform-carousel-dots">
        {SLIDES.map((s, i) => (
          <button
            key={i}
            onClick={() => setIndex(i)}
            data-testid={`platform-carousel-dot-${i}`}
            aria-label={s.platform}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              i === index ? "w-8 bg-emerald-300" : "w-1.5 bg-white/30 hover:bg-white/60"
            }`}
          />
        ))}
      </div>
    </div>
  );
};
